import React, { useState } from 'react'
import { apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import AlertUI from '../../generic/AlertUI'
import { IconButton, Stack, ToggleButton, Tooltip, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import CancelIcon from '@mui/icons-material/Cancel'
import DoneIcon from '@mui/icons-material/Done'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useToggle } from '../../generic/hooks/useToggle'

const PendingRequests = (props) => {
  const { toolbar } = props
  const [AlertElement, showAlert] = AlertUI()
  const [selectedRow, setSelectedRow] = useState({})
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showPendingHistory, setShowPendingHistory] = useToggle()

  const { t_ } = useSmartTranslation()

  const getTableData = async (pagination) => {
    try {
      return await apiPost('pendingRequests/' + showPendingHistory, pagination)
    } catch (error) {
      showAlert({
        title: t_('Error fetching requests'),
        message: error.message,
        severity: 'error'
      })
      return { content: [], totalElements: 0 }
    }
  }

  const getDevicePosition = (device, position) => {
    if (!position) {
      return device
    }
    return <Stack direction='row' gap={3}><div>{device}</div><div>{position}</div></Stack>
  }

  const columns = [
    {
      name: t_('From'),
      field: 'deviceFrom',
      readOnly: true,
      render: (row) => {
        return getDevicePosition(row.deviceFrom, row.positionFrom)
      }
    },
    {
      name: t_('To'),
      field: 'deviceTo',
      readOnly: true,
      render: (row) => {
        return getDevicePosition(row.deviceTo, row.positionTo)
      }
    },
    {
      name: t_('Creation date'),
      field: 'creationDate',
      type: 'date',
      readOnly: true
    },
    {
      name: t_('Start date'),
      field: 'startDate',
      type: 'datetime',
      readOnly: true
    },
    {
      name: t_('End date'),
      field: 'endDate',
      type: 'datetime',
      readOnly: true
    },
    {
      name: t_('Cancellation date'),
      field: 'cancellationDate',
      type: 'datetime',
      readOnly: true
    }
  ]

  const actions = {
    custom: [
      {
        contextMenu: false,
        render: (row) => {
          const isCompleted = row.startDate && ((row.endDate && !row.cancellationDate) || (!row.endDate && row.cancellationDate))
          if (isCompleted) {
            return (
              <Tooltip title={t_('Completed')}>
                <span><IconButton disabled><DoneIcon /></IconButton></span>
              </Tooltip>
            )
          } else {
            const isCancellable = row.startDate && !row.endDate && !row.cancellationDate
            return (
              <Tooltip title={t_('Cancel request')}>
                <IconButton
                  disabled={!isCancellable}
                  onClick={() => {
                    setSelectedRow(row)
                    setShowCancelDialog(true)
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            )
          }
        }
      }
    ]
  }

  const customToolbar = {
    ...toolbar,
    renderSticky: {
      fetchFunction: () => {
        return (
          <ToggleButton
            color='primary'
            variant='outlined'
            selected={showPendingHistory}
            onChange={() => setShowPendingHistory(!showPendingHistory)}
          >
            {t_('Show all')}
          </ToggleButton>
        )
      }
    }
  }

  const handleAccept = async () => {
    try {
      await apiPut('commandMove/cancel/' + selectedRow.id)
      showAlert({
        title: t_('Success'),
        message: t_('Request successfully cancelled'),
        severity: 'success'
      })
      setSelectedRow({})
    } catch (error) {
      showAlert({
        title: t_('Error cancelling request'),
        message: t_(error.message),
        severity: 'error'
      })
    }
    setShowCancelDialog(false)
  }

  return (
    <>
      {AlertElement}
      <SmartDialog
        setOpen={showCancelDialog}
        title={createDialogTitle({ title: t_('Cancel Request?'), icon: CancelIcon, color: 'primary' })}
        cancelCallback={() => setShowCancelDialog(false)}
        renderComponent={<Typography>{t_('Are you sure you want to cancel this request?')}</Typography>}
        acceptCallback={handleAccept}
      />
      <SmartMateriaUITable
        columns={columns}
        dataFetch={getTableData}
        actions={actions}
        multipleSelection={false}
        pageable
        toolbar={customToolbar}
        const sortOptions={{
          default: {
            field: 'creationDate',
            direction: -1
          }
        }}
      />
    </>
  )
}

export default PendingRequests
